/**
 * Public Proposal Settings Modal
 * ------------------------------
 * Single popup that consolidates the things an employee toggles BEFORE
 * sending a quote. The two surfaces fed by these settings live in different
 * Firestore docs (intentionally, see plan), so this modal writes to both
 * under the hood and labels each section with where it shows up:
 *
 *   · Timeline phase durations  → clientPortals/{portalId}.projects.{dealId}.timelineOverrides
 *                                 (rendered by the client portal during the quote stage)
 *   · Show timber sizes         → proposalBuilders/{dealId}.publicSettings.showTimberSizes
 *                                 (rendered by the public proposal page /proposal/:slug)
 *   · Show cost per square foot → proposalBuilders/{dealId}.publicSettings.showCostPerSqFt
 *                                 (requires projectSquareFootage on the proposal)
 *
 * The modal owns its own draft state — opening it snapshots the current
 * values, edits stay local, and only Save commits to Firestore.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  GanttChart,
  Layers3,
  Square,
  Settings2,
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import './PublicProposalSettingsModal.css';

/** Single timeline phase shape — matches PortalProject.timelineOverrides[]. */
export interface TLPhase {
  id: string;
  label: string;
  durationWeeks: number;
  bufferWeeks: number;
  subPhases?: Array<{
    id: string;
    label: string;
    durationWeeks: number;
  }>;
}

export interface PublicProposalSettingsModalProps {
  open: boolean;
  onClose: () => void;
  /** Portal doc id (clientPortals/{portalId}). When null, the timeline section is read-only. */
  portalId: string | null;
  /** Active deal id — used for both write paths. When null, modal can't save. */
  dealId: string | null;
  /** Current persisted timeline phases (from active project or global defaults). */
  initialTimelinePhases: TLPhase[];
  /** Current persisted value of `proposalBuilders/{dealId}.publicSettings.showTimberSizes`. */
  initialShowTimberSizes: boolean;
  /** Current persisted value of `proposalBuilders/{dealId}.publicSettings.showCostPerSqFt`. */
  initialShowCostPerSqFt?: boolean;
  /**
   * True when there is a `proposalBuilders/{dealId}` doc to write to. When
   * false, the timber section is shown disabled with an explanatory hint
   * instead of letting the user toggle into a no-op.
   */
  hasProposalDoc: boolean;
  /**
   * Optional callback after a successful save so the parent can refresh its
   * local state without re-fetching. Called with the values that were just
   * persisted.
   */
  onSaved?: (next: { phases: TLPhase[]; showTimberSizes: boolean; showCostPerSqFt: boolean }) => void;
}

const PublicProposalSettingsModal: React.FC<PublicProposalSettingsModalProps> = ({
  open,
  onClose,
  portalId,
  dealId,
  initialTimelinePhases,
  initialShowTimberSizes,
  initialShowCostPerSqFt = false,
  hasProposalDoc,
  onSaved,
}) => {
  // Draft state — snapshot the props on each open so cancelling truly
  // discards changes (we re-seed via the keyed effect below when the modal
  // is reopened).
  const [phases, setPhases] = useState<TLPhase[]>(initialTimelinePhases);
  const [showTimberSizes, setShowTimberSizes] = useState<boolean>(initialShowTimberSizes);
  const [showCostPerSqFt, setShowCostPerSqFt] = useState<boolean>(!!initialShowCostPerSqFt);
  const [productionExpanded, setProductionExpanded] = useState(false);

  const [saving, setSaving] = useState(false);
  // status: 'idle' | 'saved' | 'partial' | 'error' — drives footer message.
  const [status, setStatus] = useState<'idle' | 'saved' | 'partial' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState<string>('');

  // Re-seed draft state whenever the modal opens (or the source values change
  // while it's already open — rare but covers parent re-fetch races).
  useEffect(() => {
    if (!open) return;
    setPhases(initialTimelinePhases);
    setShowTimberSizes(initialShowTimberSizes);
    setShowCostPerSqFt(!!initialShowCostPerSqFt);
    setStatus('idle');
    setStatusMsg('');
  }, [open, initialTimelinePhases, initialShowTimberSizes, initialShowCostPerSqFt]);

  // ESC closes (unless we're mid-save — avoids losing user input).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const totalWeeks = useMemo(
    () => phases.reduce((s, p) => s + (Number(p.durationWeeks) || 0) + (Number(p.bufferWeeks) || 0), 0),
    [phases],
  );

  const dirty = useMemo(() => {
    if (showTimberSizes !== initialShowTimberSizes) return true;
    if (showCostPerSqFt !== !!initialShowCostPerSqFt) return true;
    if (phases.length !== initialTimelinePhases.length) return true;
    for (let i = 0; i < phases.length; i++) {
      const a = phases[i];
      const b = initialTimelinePhases[i];
      if (!b) return true;
      if (a.durationWeeks !== b.durationWeeks || a.bufferWeeks !== b.bufferWeeks) return true;
      if ((a.subPhases?.length || 0) !== (b.subPhases?.length || 0)) return true;
      if (a.subPhases) {
        for (let j = 0; j < a.subPhases.length; j++) {
          if (a.subPhases[j].durationWeeks !== b.subPhases![j].durationWeeks) return true;
        }
      }
    }
    return false;
  }, [phases, showTimberSizes, showCostPerSqFt, initialTimelinePhases, initialShowTimberSizes, initialShowCostPerSqFt]);

  // ─── Save ──────────────────────────────────────────────────────────────
  // Two writes happen in parallel; we surface partial success as a warning
  // rather than rolling back, since the two domains are independent and a
  // user would rather know that one of them landed than have nothing happen.
  const handleSave = async () => {
    if (!dealId) return;
    setSaving(true);
    setStatus('idle');
    setStatusMsg('');

    const writes: Array<Promise<{ which: 'timeline' | 'public'; ok: boolean; err?: unknown }>> = [];

    if (portalId) {
      writes.push(
        (async () => {
          try {
            await updateDoc(doc(db, 'clientPortals', portalId), {
              [`projects.${dealId}.timelineOverrides`]: phases,
              updatedAt: Timestamp.now(),
            });
            return { which: 'timeline' as const, ok: true };
          } catch (err) {
            console.error('[PublicProposalSettingsModal] timeline save failed', err);
            return { which: 'timeline' as const, ok: false, err };
          }
        })(),
      );
    }

    if (hasProposalDoc) {
      writes.push(
        (async () => {
          try {
            await updateDoc(doc(db, 'proposalBuilders', dealId), {
              'publicSettings.showTimberSizes': !!showTimberSizes,
              'publicSettings.showCostPerSqFt': !!showCostPerSqFt,
              updatedAt: Timestamp.now(),
            });
            return { which: 'public' as const, ok: true };
          } catch (err) {
            console.error('[PublicProposalSettingsModal] public toggle save failed', err);
            return { which: 'public' as const, ok: false, err };
          }
        })(),
      );
    }

    const results = await Promise.all(writes);
    const failed = results.filter(r => !r.ok);
    setSaving(false);

    if (failed.length === 0 && results.length > 0) {
      setStatus('saved');
      setStatusMsg('All settings saved.');
      onSaved?.({ phases, showTimberSizes, showCostPerSqFt });
      // Auto-close on full success after a brief flash so the user sees
      // confirmation, but doesn't have to dismiss manually.
      setTimeout(() => onClose(), 700);
    } else if (failed.length > 0 && failed.length < results.length) {
      setStatus('partial');
      const which = failed.map(f => (f.which === 'timeline' ? 'timeline' : 'public proposal')).join(' and ');
      setStatusMsg(`Saved partially — ${which} failed. Try again.`);
      onSaved?.({ phases, showTimberSizes, showCostPerSqFt });
    } else {
      setStatus('error');
      setStatusMsg('Save failed. Check connection and try again.');
    }
  };

  if (!open) return null;

  const canSave = !!dealId && (!!portalId || hasProposalDoc) && dirty && !saving;

  return (
    <div
      className="pps-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Public proposal settings"
      onClick={e => {
        // Click outside dismisses — only when the click started AND ended on
        // the overlay (avoid accidental dismissal on drag-select inside the
        // modal that ends on the backdrop).
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="pps-modal" onClick={e => e.stopPropagation()}>
        <header className="pps-header">
          <span className="pps-header-icon">
            <Settings2 size={18} />
          </span>
          <div className="pps-header-text">
            <h3>Client-Facing Settings</h3>
            <p className="pps-header-sub">
              Control what your client sees on the portal and the public proposal page.
            </p>
          </div>
          <button type="button" className="pps-close" onClick={onClose} aria-label="Close" title="Close (Esc)">
            <X size={18} />
          </button>
        </header>

        <div className="pps-body">
          {/* ── Timeline ──────────────────────────────────────────────────── */}
          <section className="pps-section" aria-labelledby="pps-timeline-title">
            <div className="pps-section-head">
              <span className="pps-section-icon"><GanttChart size={14} /></span>
              <div className="pps-section-text">
                <h4 className="pps-section-title" id="pps-timeline-title">
                  Timeline
                  <span className="pps-section-where">Client portal</span>
                </h4>
                <p className="pps-section-desc">
                  Customize phase durations for this project. Overrides the global defaults.
                </p>
              </div>
            </div>

            <div className="pps-timeline-list">
              {phases.map((phase, idx) => {
                const hasSubs = !!phase.subPhases && phase.subPhases.length > 0;
                const isProduction = phase.id === 'in-production';
                return (
                  <div key={phase.id} className="pps-phase-row">
                    <div className="pps-phase-row-main">
                      {isProduction && (
                        <button
                          type="button"
                          className="pps-phase-toggle"
                          onClick={() => setProductionExpanded(v => !v)}
                          aria-expanded={productionExpanded}
                          aria-label={productionExpanded ? 'Hide sub-phases' : 'Show sub-phases'}
                        >
                          {productionExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                      )}
                      <span className="pps-phase-label">{phase.label}</span>
                      <div className="pps-num-group">
                        <input
                          type="number"
                          min={1}
                          value={phase.durationWeeks}
                          disabled={hasSubs}
                          aria-label={`${phase.label} duration in weeks`}
                          onChange={e => {
                            const next = [...phases];
                            next[idx] = { ...phase, durationWeeks: Math.max(1, Number(e.target.value) || 1) };
                            setPhases(next);
                          }}
                          className="pps-num-input"
                        />
                        <span className="pps-num-suffix">wks</span>
                      </div>
                      <div className="pps-num-group">
                        <input
                          type="number"
                          min={0}
                          value={phase.bufferWeeks}
                          aria-label={`${phase.label} buffer in weeks`}
                          onChange={e => {
                            const next = [...phases];
                            next[idx] = { ...phase, bufferWeeks: Math.max(0, Number(e.target.value) || 0) };
                            setPhases(next);
                          }}
                          className="pps-num-input"
                        />
                        <span className="pps-num-suffix">buf</span>
                      </div>
                    </div>
                    {isProduction && productionExpanded && phase.subPhases && (
                      <div className="pps-subphases">
                        {phase.subPhases.map((sub, sIdx) => (
                          <div key={sub.id} className="pps-subphase-row">
                            <span className="pps-subphase-label">{sub.label}</span>
                            <div className="pps-num-group">
                              <input
                                type="number"
                                min={1}
                                value={sub.durationWeeks}
                                aria-label={`${sub.label} duration in weeks`}
                                onChange={e => {
                                  const nw = Math.max(1, Number(e.target.value) || 1);
                                  const next = [...phases];
                                  const subs = [...phase.subPhases!];
                                  subs[sIdx] = { ...sub, durationWeeks: nw };
                                  // Production duration = sum of sub-phases (mirrors existing behavior).
                                  next[idx] = {
                                    ...phase,
                                    subPhases: subs,
                                    durationWeeks: subs.reduce((s, x) => s + x.durationWeeks, 0),
                                  };
                                  setPhases(next);
                                }}
                                className="pps-num-input"
                              />
                              <span className="pps-num-suffix">wks</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="pps-timeline-total">Total: ~{totalWeeks} weeks</div>
          </section>

          {/* ── Timber sizes ──────────────────────────────────────────────── */}
          <section className="pps-section" aria-labelledby="pps-timber-title">
            <div className="pps-section-head">
              <span className="pps-section-icon"><Layers3 size={14} /></span>
              <div className="pps-section-text">
                <h4 className="pps-section-title" id="pps-timber-title">
                  Timber sizes
                  <span className="pps-section-where pps-section-where--public">Public proposal</span>
                </h4>
                <p className="pps-section-desc">
                  Lets the client tap a structure and see a clean list of timbers used —
                  Name, Grade, Dryness, and the cross-section (e.g. <strong>4x8</strong>).
                  Lengths, quantities, and pricing are never exposed.
                </p>
              </div>
            </div>

            <div className="pps-toggle-row">
              <div className="pps-toggle-text">
                <p className="pps-toggle-title">Let the client view timber sizes</p>
                <p className="pps-toggle-desc">
                  {hasProposalDoc
                    ? 'Off by default. Turn on once your timber list is finalized.'
                    : 'Build a proposal first to enable this.'}
                </p>
              </div>
              <label className="pps-switch" aria-label="Toggle let client view timber sizes">
                <input
                  type="checkbox"
                  checked={showTimberSizes}
                  disabled={!hasProposalDoc}
                  onChange={e => setShowTimberSizes(e.target.checked)}
                />
                <span className="pps-switch-track" aria-hidden />
                <span className="pps-switch-thumb" aria-hidden />
              </label>
            </div>
          </section>

          <section className="pps-section" aria-labelledby="pps-psf-title">
            <div className="pps-section-head">
              <span className="pps-section-icon"><Square size={14} /></span>
              <div className="pps-section-text">
                <h4 className="pps-section-title" id="pps-psf-title">
                  Cost per square foot
                  <span className="pps-section-where pps-section-where--public">Public proposal</span>
                </h4>
                <p className="pps-section-desc">
                  Shows a <strong>$/sf</strong> badge next to the client total when a project
                  square footage is set on the proposal.
                </p>
              </div>
            </div>

            <div className="pps-toggle-row">
              <div className="pps-toggle-text">
                <p className="pps-toggle-title">Show cost per square foot</p>
                <p className="pps-toggle-desc">
                  {hasProposalDoc
                    ? 'Off by default. Requires a project square footage on the proposal.'
                    : 'Build a proposal first to enable this.'}
                </p>
              </div>
              <label className="pps-switch" aria-label="Toggle show cost per square foot">
                <input
                  type="checkbox"
                  checked={showCostPerSqFt}
                  disabled={!hasProposalDoc}
                  onChange={e => setShowCostPerSqFt(e.target.checked)}
                />
                <span className="pps-switch-track" aria-hidden />
                <span className="pps-switch-thumb" aria-hidden />
              </label>
            </div>
          </section>
        </div>

        <footer className="pps-footer">
          <span
            className={
              status === 'saved'
                ? 'pps-footer-msg pps-footer-msg--ok'
                : status === 'partial'
                  ? 'pps-footer-msg pps-footer-msg--warn'
                  : status === 'error'
                    ? 'pps-footer-msg pps-footer-msg--err'
                    : 'pps-footer-msg'
            }
            role="status"
            aria-live="polite"
          >
            {status === 'saved' && (<><Check size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />{statusMsg}</>)}
            {status === 'partial' && (<><AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />{statusMsg}</>)}
            {status === 'error' && (<><AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />{statusMsg}</>)}
            {status === 'idle' && (dirty ? 'Unsaved changes' : 'No changes')}
          </span>
          <button type="button" className="pps-btn pps-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="pps-btn pps-btn--primary"
            onClick={handleSave}
            disabled={!canSave}
            title={!dealId ? 'No active deal' : !dirty ? 'Nothing to save' : undefined}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default PublicProposalSettingsModal;
